import { Injectable, inject } from '@angular/core';
import { APPNAME } from '../Constants/app-injection-token';
import { getRumClient } from './rum-client';
import { SolvaceRumAction, SolvaceRumContext, SolvaceRumFlow, SolvaceRumModule, SolvaceRumOrigin } from './solvace-rum.types';

const FLOW_STORAGE_PREFIX = 'solvace-rum-flow:';
const MAX_DURATION_MS = 120000;

interface SolvaceRumPendingFlow {
  startedAt: number;
  module?: SolvaceRumModule;
}

@Injectable({ providedIn: 'root' })
export class SolvaceRumService {
  readonly #appName = inject(APPNAME, { optional: true });

  public readonly origin: SolvaceRumOrigin = this.#appName === 'myworkspace' ? 'home' : 'module';

  public track(action: SolvaceRumAction, context: SolvaceRumContext): void {
    getRumClient()?.addAction(action, { solvace: { origin: this.origin, ...context } });
  }

  public startTimer(): number {
    return performance.now();
  }

  public trackDuration(action: SolvaceRumAction, startedAt: number, context: SolvaceRumContext): void {
    this.#trackWithinLimit(action, performance.now() - startedAt, context);
  }

  public startFlow(flow: SolvaceRumFlow, module?: SolvaceRumModule): boolean {
    try {
      const pending: SolvaceRumPendingFlow = { startedAt: Date.now(), module };
      localStorage.setItem(FLOW_STORAGE_PREFIX + flow, JSON.stringify(pending));
      return true;
    } catch {
      return false;
    }
  }

  public completeFlow(flow: SolvaceRumFlow, action: SolvaceRumAction, context: SolvaceRumContext): void {
    const pending = this.#takeFlow(flow);
    if (!pending || (pending.module && pending.module !== context.module)) return;

    this.#trackWithinLimit(action, Date.now() - pending.startedAt, context);
  }

  public discardFlow(flow: SolvaceRumFlow): void {
    this.#takeFlow(flow);
  }

  #trackWithinLimit(action: SolvaceRumAction, durationMs: number, context: SolvaceRumContext): void {
    if (!Number.isFinite(durationMs) || durationMs < 0 || durationMs > MAX_DURATION_MS) return;
    this.track(action, { ...context, duration_ms: Math.round(durationMs) });
  }

  #takeFlow(flow: SolvaceRumFlow): SolvaceRumPendingFlow | null {
    try {
      const key = FLOW_STORAGE_PREFIX + flow;
      const raw = localStorage.getItem(key);
      localStorage.removeItem(key);
      const pending = raw ? (JSON.parse(raw) as SolvaceRumPendingFlow) : null;
      return Number.isFinite(pending?.startedAt) ? pending : null;
    } catch {
      return null;
    }
  }
}
